import { Form, FormApi, FormState } from 'informed';
import useSearchString from '../../hooks/UrlParamsHandler/useSearchParams';
import { useEffect, useRef, useState } from 'react';
import { getDefaultOption, useUrlParameters } from './calendarPageUtils';
import { useQuery } from 'react-query';
import getResponseArray from '@tymbe/utils/getResponseArray';
import { CompanyData } from '@tymbe/schema/company.interface';
import CompanyBranchSelectorFields from './components/CompanyBranchSelectorFields';
import apiClient from '../../apiClient';

type CompanyBranchDepartmentSelectorProps = {
  isFloating?: boolean;
  showLabels?: boolean;
  className?: string;
  start: string;
  end: string;
}

export const CompanyBranchDepartmentSelector = ({
  showLabels = true,
  isFloating = false,
  className,
  start,
  end,
}: CompanyBranchDepartmentSelectorProps) => {
  const [searchParams, setSearchParams] = useSearchString();
  const ref = useRef<FormApi<object>>(undefined);
  const [companyIdFromURL, branchofficeIdFromURL, departmentIdFromURL] =
    useUrlParameters();

  useQuery({
    queryKey: ['company', 'branchoffice', 'department', 'list'],
    queryFn: async () =>
      apiClient.service('company').find({
        query: {
          $eager: 'branchoffice(hasChild).child',
          $limit: -1,
          $sort: { name: 1 },
        },
      }),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    staleTime: 0,
    onSuccess: (companies) => {
      const companiesArray = getResponseArray(companies);
      if (companiesArray.length === 1) {
        setInitialValuesFromCompanies(companiesArray);
      }
    },
  });

  const setInitialValuesFromCompanies = (companiesArray: CompanyData[]) => {
    const selectedCompany = companiesArray[0];
    const branchoffices = selectedCompany?.branchoffice || [];
    const departments = branchoffices[0]?.child || [];

    const defaultCompany = getDefaultOption(companiesArray);
    const defaultBranchoffices = getDefaultOption(branchoffices);
    const defaultDepartments = defaultBranchoffices !== undefined ? getDefaultOption(departments) : undefined;

    onCompanyBranchSelectorFormChange({
      values: {
        company: defaultCompany,
        branchoffice: defaultBranchoffices,
        department: defaultDepartments,
      },
    });

    setInitialValues({
      branchoffice: defaultBranchoffices,
      company: defaultCompany,
      department: defaultDepartments,
    });
  };

  const [initialValues, setInitialValues] = useState({
    company: companyIdFromURL,
    branchoffice: branchofficeIdFromURL,
    department: departmentIdFromURL,
  });

  const onCompanyBranchSelectorFormChange = ({
    values,
  }: Partial<
    FormState<{ company?: number; branchoffice?: number; department?: number }>
  >) => {
    const newParams = new URLSearchParams(searchParams);

    if (values) {
      const { company, branchoffice, department } = values;

      if (company) newParams.set('company_id', company.toString());
      else newParams.delete('company_id');

      if (branchoffice)
        newParams.set('branchoffice_id', branchoffice.toString());
      else newParams.delete('branchoffice_id');

      if (department) newParams.set('department_id', department.toString());
      else newParams.delete('department_id');
    }

    setSearchParams(newParams);
  };

  useEffect(() => {
    const companyBranchSelectorFormValuesFromURL = {
      company: companyIdFromURL,
      branchoffice: branchofficeIdFromURL,
      department: departmentIdFromURL,
    };
    ref.current?.setValues(companyBranchSelectorFormValuesFromURL);
  }, [companyIdFromURL, branchofficeIdFromURL, departmentIdFromURL]);

  return (
    <div className={className}>
      <Form
        onChange={onCompanyBranchSelectorFormChange}
        formApiRef={ref}
        className="flex gap-2 py-2"
        initialValues={initialValues}
      >
        <CompanyBranchSelectorFields showLabels={showLabels} isFloating={isFloating} start={start} end={end} />
      </Form>
    </div>
  );
};
